'use client'

import PickupDeliverySelectionModal from '@/components/modals/PickupDeliverySelectionModal'
import ReminderOrderModal from '@/components/modals/ReminderOrderModal'
import { MODAL_TYPES } from '@/libs/config/modal'
import {
  ComponentType,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

// Modal registry with lazy loading functions
const modalRegistry: Record<
  string,
  () => Promise<{ default: ComponentType<any> }>
> = {
  [MODAL_TYPES.CART_DRAWER]: () => import('@/components/modals/CartDrawer'),
  [MODAL_TYPES.PICKUP_STORE_SELECTION]: () =>
    import('@/components/modals/PickupStoreSelectionModal'),
  [MODAL_TYPES.DATE_TIME_SELECTION]: () =>
    import('@/components/modals/DateTimeSelectionModal'),
  [MODAL_TYPES.DELIVERY_ADDRESS_SELECTION]: () =>
    import('@/components/modals/DeliverySelectAddressModal'),
  [MODAL_TYPES.REFRESH_BROWSER]: () =>
    import('@/components/modals/RefreshModal'),
  [MODAL_TYPES.CLEAR_CART_ITEMS]: () =>
    import('@/components/modals/ClearCartConfirmationModal'),
  [MODAL_TYPES.ERROR_MODAL]: () => import('@/components/modals/ErrorModal'),
  [MODAL_TYPES.CHECKOUT_FAILED_PROMOTION]: () =>
    import(
      '@/components/modals/CheckoutPromotionModal/CheckoutFailedPromotionModal'
    ),
  [MODAL_TYPES.CHECKOUT_PROMOTION_ITEM]: () =>
    import('@/components/modals/CheckoutPromotionModal/PromotionItemModal'),
  [MODAL_TYPES.CHECKOUT_PROMOTION_SUMMARY]: () =>
    import(
      '@/components/modals/CheckoutPromotionModal/CheckoutPromotionSummaryModal'
    ),
  [MODAL_TYPES.CHECKOUT_PROMOTION_REMOVAL]: () =>
    import(
      '@/components/modals/CheckoutPromotionModal/CheckoutPromoRemovalModal'
    ),
  [MODAL_TYPES.CHECKOUT_PROMOTION_FAILED_WITH_CARD]: () =>
    import(
      '@/components/modals/CheckoutPromotionModal/CheckoutFailedCardPromotionsModal'
    ),
  [MODAL_TYPES.CHECKOUT_PROMOTION_SELECTION]: () =>
    import('@/components/modals/CheckoutPromotionModal'),
  [MODAL_TYPES.PROMOTION_DETAILS]: () =>
    import('@/components/modals/PromotionDetailsModal'),
  [MODAL_TYPES.MARKETING_POPUP]: () =>
    import('@/components/modals/MarketingPopupModal'),
  [MODAL_TYPES.NEWSLETTER_SIGNUP]: () =>
    import('@/components/modals/NewsletterSignupModal'),
  [MODAL_TYPES.CHECKOUT_EXIT_CONFIRMATION]: () =>
    import('@/components/modals/CheckoutExitConfirmationModal'),
  [MODAL_TYPES.FOREIGN_PHONE_NUMBER]: () =>
    import('@/components/modals/ForeignPhoneNumberModal'),
  [MODAL_TYPES.VOUCHERS_SELECTOR]: () =>
    import('@/components/modals/VouchersSelectionModal'),
  [MODAL_TYPES.VOUCHER_DETAILS]: () =>
    import('@/components/modals/VouchersSelectionModal/VoucherDetailsModal'),
  [MODAL_TYPES.LOADING_OVERLAY]: () =>
    import('@/components/modals/CheckoutLoadingOverLay'),
  [MODAL_TYPES.SELECT_CALENDAR]: () =>
    import('@/components/modals/SelectCalendarModal'),
  [MODAL_TYPES.DOWNLOAD_CALENDAR_FILE]: () =>
    import('@/components/modals/DownloadCalendarFileModal'),
  [MODAL_TYPES.SHARE_APP]: () => import('@/components/modals/ShareAppModal'),
  [MODAL_TYPES.CONTACT_RESTAURANT]: () =>
    import('@/components/modals/ContactRestaurantModal'),
  [MODAL_TYPES.NEED_HELP]: () => import('@/components/modals/NeedHelpModal'),
  [MODAL_TYPES.CONTACT_LOGISTIC]: () =>
    import('@/components/modals/ContactLogisticModal'),
}

// Cache for loaded modal components to avoid re-importing
const modalCache: Record<string, ComponentType<any>> = {}

interface ModalContextType {
  openModal: (
    modalName: string,
    props?: Record<string, unknown>,
    scrollable?: boolean
  ) => Promise<void>
  closeModal: () => void
  activeModal: string | null
  modalProps: Record<string, unknown>
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    // Provide a fallback to prevent app crashes
    console.warn(
      'useModal must be used within a ModalProvider. Providing fallback.'
    )
    return {
      openModal: async () => {
        console.warn('Modal context not available, modal action ignored')
      },
      closeModal: () => {
        console.warn('Modal context not available, close action ignored')
      },
      activeModal: null,
      modalProps: {},
    }
  }
  return context
}

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [activeModal, setActiveModal] = useState<string | null>(null)
  const [modalProps, setModalProps] = useState({})
  const [LoadedModalComponent, setLoadedModalComponent] =
    useState<ComponentType<any> | null>(null)

  // Prefetch critical modals in the background after initial load
  useEffect(() => {
    const prefetchCriticalModals = async () => {
      // Wait a bit to not interfere with initial page load
      await new Promise((resolve) => setTimeout(resolve, 2000))

      const criticalModals = [
        MODAL_TYPES.DELIVERY_ADDRESS_SELECTION,
        MODAL_TYPES.PICKUP_STORE_SELECTION,
        MODAL_TYPES.DATE_TIME_SELECTION,
        MODAL_TYPES.CART_DRAWER,
        MODAL_TYPES.LOADING_OVERLAY,
      ]

      for (const modalName of criticalModals) {
        // Only prefetch if not already cached
        if (!modalCache[modalName] && modalRegistry[modalName]) {
          try {
            const modalModule = await modalRegistry[modalName]()
            if (modalModule.default) {
              modalCache[modalName] = modalModule.default
            }
          } catch (error) {
            console.warn(`Failed to prefetch modal ${modalName}:`, error)
          }
        }
      }
    }

    prefetchCriticalModals()
  }, [])

  const openModal = useCallback(
    async (modalName: string, props = {}, scrollable?: boolean) => {
      setActiveModal(modalName)
      setModalProps(props)
      if (typeof document !== 'undefined' && !scrollable)
        document.body.style.overflow = 'hidden'

      // Handle pre-imported modals
      if (modalName === MODAL_TYPES.PICKUP_DELIVERY_SELECTION) {
        setLoadedModalComponent(() => PickupDeliverySelectionModal)
        return
      }

      if (modalName === MODAL_TYPES.REMINDER_ORDER) {
        setLoadedModalComponent(() => ReminderOrderModal)
        return
      }

      // Check cache first
      const cachedComponent = modalCache[modalName]
      if (cachedComponent) {
        setLoadedModalComponent(() => cachedComponent)
        return
      }

      // Handle lazy-loaded modals
      const modalLoader = modalRegistry[modalName]
      if (modalLoader) {
        try {
          const modalModule = await modalLoader()
          const Component = modalModule.default
          modalCache[modalName] = Component // Cache for future use
          setLoadedModalComponent(() => Component)
        } catch (error) {
          console.error(`Failed to load modal "${modalName}":`, error)

          // Check if this is a chunk loading error
          if (
            error?.message?.includes('Loading chunk') ||
            error?.message?.includes(
              'Failed to fetch dynamically imported module'
            ) ||
            error?.name === 'ChunkLoadError'
          ) {
            // This is likely a chunk loading issue, let the global handler deal with it
            console.warn(
              'Chunk loading error detected in modal loading - will be handled by global error handler'
            )
          }

          setLoadedModalComponent(null)
        }
      } else {
        console.warn(`Modal type "${modalName}" not found in registry.`)
        setLoadedModalComponent(null)
      }
    },
    []
  )

  const closeModal = useCallback(() => {
    setActiveModal(null)
    setModalProps({})
    setLoadedModalComponent(null)
    if (typeof document !== 'undefined') document.body.style.overflow = 'auto'
  }, [])

  const renderModal = () => {
    if (!activeModal) return null

    // Show backdrop immediately, even if modal is still loading
    if (!LoadedModalComponent) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white"></div>
          </div>
        </div>
      )
    }

    // Ensure modalProps does not override the essential modal controls
    const { ...restModalProps } = modalProps

    return (
      <LoadedModalComponent
        modal={{
          show: true,
          onClose: closeModal,
          onClosed: closeModal,
        }}
        {...restModalProps}
      />
    )
  }

  return (
    <ModalContext.Provider
      value={{ openModal, closeModal, activeModal, modalProps }}
    >
      {children}
      {renderModal()}
    </ModalContext.Provider>
  )
}
